import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { motion, useAnimation } from 'framer-motion';
import { Box, Container, Flex, Image, Stack, useColorModeValue } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import StickyHeader from '../components/bar/StickyHeader';
import './LandingPage.css';

const MotionBox = motion(Box);
const MotionHeading = motion('h1');
const MotionText = motion('p');

const PROFILE_CARD_WIDTH = 240;
const PROFILE_CARD_GAP = 18;
const PROFILE_CARD_COUNT = 4;
// 무한 루프용: 카드 1세트 길이만큼 이동하면 다음 세트가 정확히 이어짐
const PROFILE_CARD_TRAVEL_X = (PROFILE_CARD_WIDTH + PROFILE_CARD_GAP) * PROFILE_CARD_COUNT;

const profileCards = [
  { src: '/assets/landing/profile_card_01.png', alt: 'bettie profile card 1' },
  { src: '/assets/landing/profile_card_02.png', alt: 'bettie profile card 2' },
  { src: '/assets/landing/profile_card_03.png', alt: 'bettie profile card 3' },
  { src: '/assets/landing/profile_card_04.png', alt: 'bettie profile card 4' }
];

const BenefitItem = ({ number, month, title, description, delay, imageUrl }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <MotionBox
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
        hidden: { opacity: 0, y: 20 }
      }}
      className="benefit-item"
    >
      <div className="flex items-center gap-[9px] mb-[12px]">
        <p className="text-[13px] font-bold text-primary-light dark:text-primary-dark">
          POINT {number}
        </p>
        <div className="text-on-varified bg-varified rounded-[30px] text-[11px] leading-[13px] font-semibold px-[8px] py-[4px]">
          {month} OPEN
        </div>
      </div>

      <h2 className="text-[20px] leading-[24px] font-semibold text-text-main-lv1 mb-[24px] word-break-keep-all">
        {title}
      </h2>

      <Box
        className="benefit-image-box"
        mb={6}
        borderRadius="24px"
        overflow="hidden"
        bg="gray.100"
        h="220px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Image
          src={imageUrl}
          alt={typeof title === 'string' ? title : 'benefit image'}
          objectFit="cover"
          w="100%"
          h="100%"
          fallback={<Box w="100%" h="100%" bg="gray.50" />}
        />
      </Box>

      <p className="text-[14px] font-medium leading-[22px] text-text-main-lv4 whitespace-pre-line">
        {description}
      </p>
    </MotionBox>
  );
};

const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bg = useColorModeValue('white', '#0D0E12');
  const textColor = useColorModeValue('gray.900', 'white');

  return (
    <Box
      bg={bg}
      color={textColor}
      className="landing-container pt-[env(safe-area-inset-top)]"
      overflowX="hidden"
    >
      <StickyHeader showBackButton={true} />
      {/* 1. Header Section */}
      <Container maxW="container.sm" pt="80px" pb="40px" textAlign="center">
        <MotionHeading
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-[26px] leading-[31px] font-bold text-text-main-lv1 whitespace-pre-line word-break-keep-all"
        >
          {t('Landing.title')}
        </MotionHeading>
        <MotionText
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-[15px] leading-[18px] font-medium text-text-main-lv4 mt-[14px] whitespace-pre-line word-break-keep-all"
        >
          {t('Landing.description')}
        </MotionText>
      </Container>
      {/* 2. Visual Section (Profile Card) */}
      <Flex justify="center" align="center" pb="10px" className="visual-section">
        <MotionBox
          initial={{ opacity: 0, scale: 0.9, rotateY: -10 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{ duration: 1, delay: 0.5, type: 'spring' }}
          className="profile-card-viewport"
        >
          <MotionBox
            animate={{ x: [0, -PROFILE_CARD_TRAVEL_X] }}
            transition={{
              duration: 15,
              ease: 'linear',
              repeat: Number.POSITIVE_INFINITY
            }}
            className="profile-card-track flex items-center"
          >
            {[...profileCards, ...profileCards].map((card, idx) => (
              <img
                key={`${card.src}-${idx}`}
                src={card.src}
                alt={card.alt}
                className="profile-card-image scale-90"
              />
            ))}
          </MotionBox>
        </MotionBox>
      </Flex>
      {/* 3. Benefits Section */}
      <Container maxW="container.sm" pb="60px" px="24px">
        <Stack spacing="60px">
          <BenefitItem
            // month={t('Landing.month.first')}
            title={t('Landing.bettie.title')}
            description={t('Landing.bettie.description')}
            delay={0.1}
            imageUrl="/assets/landing/bettie.png"
          />
          {/* <BenefitItem
            number="02"
            month={t('Landing.month.second')}
            title={t('Landing.scene.title')}
            description={t('Landing.scene.description')}
            delay={0.2}
            imageUrl="/assets/landing/scene.png"
          />
          <BenefitItem
            number="03"
            month={t('Landing.month.third')}
            title={t('Landing.randomChat.title')}
            description={t('Landing.randomChat.description')}
            delay={0.3}
            imageUrl="/assets/landing/fan_buddy.png"
          /> */}
        </Stack>
      </Container>
      {/* 4. CTA Section */}
      <Box className="cta-sticky-container px-[20px] pt-[20px] pb-[calc(20px+env(safe-area-inset-bottom))]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold cta-shadow-pulse"
          onClick={() => navigate('/fan-buddy/intro')}
        >
          {t('Landing.createProfileCard')}
        </Button>
      </Box>
      <Box h="120px" /> {/* Bottom Spacing for Sticky Button */}
    </Box>
  );
};

export default LandingPage;
